import React, { FC, useCallback, useEffect, useState } from 'react';
import { Alert, Input, InputNumber } from 'antd';
import { useTranslation } from 'react-i18next';
import { FormInstance } from 'antd/lib/form';
import { isEmpty, merge } from 'lodash';

import { AddressInputGroup, CountryInput, Form, LegalTypeInput, ExpansionForm } from '~/components';
import { Beneficiary, Handbook } from '~/types';
import { useHandbook } from '~/hooks';
import { phoneValidation } from '~/utils';
import PhoneInput from '~/components/PhoneInput';

interface Props {
  form: FormInstance;
  initialValues?: Beneficiary;
  edit?: boolean;
}

const BusinessForm: FC<Props> = (props) => {
  const { form, initialValues } = props;
  const [t] = useTranslation('payouts');
  const [td] = useTranslation('documentTypeParameters');
  const dt = (key: string): string => t(`translation:${key}`);
  const { data } = useHandbook();

  // const clearDocumentParameters = () => {
  //   const {
  //     document: { legalType, legalName, type },
  //   } = form.getFieldsValue();
  //   form.resetFields(['document']);
  //   form.setFieldsValue({ document: { legalType, legalName, type } });
  // };

  const [country, setCountry] = useState('');

  const [providedLegalType, provideLegalType] = useState<any>();

  const [parameters, setParameters] = useState<{ name: string; type: string }[]>([]);

  const changeBenefitiaryType = useCallback(
    (legalType?: string, countryOptParam?: string) => {
      const resultCountry = country || countryOptParam;
      if (resultCountry && legalType && data) {
        const p = getDocumentTypeParameters(legalType, resultCountry, data);
        setParameters(p.parameters);
        provideLegalType(legalType);
        form.setFieldsValue({ document: { type: p.type } });
      } else {
        const {
          document: { legalName },
        } = form.getFieldsValue();
        form.setFieldsValue({ document: { legalName, legalType: undefined, type: undefined } });
        setParameters([]);
        provideLegalType(legalType);
      }
    },
    [country, data, form]
  );

  const changeCountry = (c: string) => {
    setCountry(c);
    changeBenefitiaryType();
  };

  // restore form state after beneficiary type change
  useEffect(() => {
    if (data) {
      const { country: countryFromForm, document: documentFromForm } = merge(
        form.getFieldsValue(),
        initialValues
      );
      if (countryFromForm && documentFromForm?.legalType && isEmpty(parameters)) {
        setCountry(countryFromForm);
        changeBenefitiaryType(documentFromForm.legalType, countryFromForm);
        form.setFieldsValue({ document: documentFromForm });
      }
    }
  }, [changeBenefitiaryType, country, form, parameters, initialValues, data]);

  useEffect(() => {
    const { country: formCountry } = form.getFieldsValue() || {};
    setCountry(formCountry);
  }, [form]);

  return (
    <>
      <Form.Item name="country" label={dt('country')} rules={[{ required: true }]}>
        <CountryInput onChange={changeCountry} placeholder={t('selectCountry')} allowClear />
      </Form.Item>
      <Form.Item name={['document', 'legalType']} label={t('legalType')}>
        <LegalTypeInput
          onChange={changeBenefitiaryType}
          countryId={country}
          disabled={!country}
          allowClear
        />
      </Form.Item>
      <Form.Item
        name={['document', 'legalName']}
        label={t('legalName')}
        rules={[{ required: true }]}
      >
        <Input placeholder={t('companyLegalName')} />
      </Form.Item>
      {providedLegalType && isEmpty(parameters) && (
        <Alert type="warning" message={t('handbookIsNotConfigured')} style={{ marginBottom: 20 }} />
      )}
      <ExpansionForm
        label={t('addIdentificationInfo')}
        // onClose={clearDocumentParameters}
        disabled={isEmpty(parameters)}
        defaultOpen={!!initialValues?.document?.type}
      >
        <Form.Item name={['document', 'type']} label={t('identificationMethod')}>
          <Input disabled />
        </Form.Item>
        {parameters?.map((item) => {
          const { name, type } = item;
          return (
            <Form.Item
              key={name}
              name={['document', name]}
              label={td(name)}
              rules={[{ required: true }]}
            >
              {(() => {
                switch (type) {
                  case 'number':
                    return <InputNumber style={fullWidth} />;
                  default:
                    return <Input />;
                }
              })()}
            </Form.Item>
          );
        })}
      </ExpansionForm>
      <ExpansionForm label={t('addAddress')} defaultOpen={!!initialValues?.address?.postalCode}>
        <AddressInputGroup excludeInputs={['country', 'cityLocalized']} />
      </ExpansionForm>
      <ExpansionForm
        label={t('addContacts')}
        defaultOpen={!!initialValues?.phoneNumber || !!initialValues?.email}
      >
        <Form.Item
          name="phoneNumber"
          label={dt('phoneNumber')}
          rules={[{ validator: phoneValidation }]}
        >
          <PhoneInput />
        </Form.Item>
        <Form.Item name="email" label={dt('email')} rules={[{ type: 'email' }]}>
          <Input />
        </Form.Item>
      </ExpansionForm>
    </>
  );
};

const EMPTY_ARRAY: any = [];

const getDocumentTypeParameters = (
  legalType: string,
  country: string,
  data: Handbook
): { type: string; parameters: { name: string; type: string }[] } => {
  const countryData = data?.countries?.find((item) => item.id === country);
  if (legalType && countryData) {
    const legalTypeData = countryData.businessLegalTypes?.find((item) => item.name === legalType);
    const primaryDocument = legalTypeData?.documents?.find((item) => item.primary);
    const mandatoryParameters = primaryDocument?.parameters?.filter((item) => item.mandatory);
    const parameters =
      mandatoryParameters?.map((item: any) => ({
        name: item.name as string,
        type: item.type as string,
      })) || EMPTY_ARRAY;
    const type = primaryDocument?.type || '';
    return { type, parameters };
  }
  return { type: '', parameters: EMPTY_ARRAY };
};

const fullWidth = { width: '100%' };

export default BusinessForm;
